// React
import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { NumericFormat } from "react-number-format";

// MUI
import {
    Box,
    TextField,
    Grid,
    Divider,
    Card,
    CardHeader,
    CardContent,
    Button,
    FormControl,
    InputLabel,
    OutlinedInput,
    InputAdornment,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

const PREFIX = "UpdateAdd";

const classes = {
    btnRoot: `${PREFIX}-btnRoot`,
    buttonContainer: `${PREFIX}-buttonContainer`,
    switch: `${PREFIX}-switch`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.btnRoot}`]: {
        border: "1px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "#2f3190",
            border: "1px solid #2f3190",
        },
        "&.Mui-disabled": {
            color: "#fff",
            borderColor: "#ffcdd2",
            background: "#ffcdd2",
        },
    },
    [`& .${classes.buttonContainer}`]: {
        display: "flex",
        justifyContent: "flex-end",
        padding: 15,
    },
    [`& .${classes.switch}`]: {
        "& .MuiSwitch-switchBase .MuiSwitch-thumb": {
            color: "#FFF",
        },
        "& .MuiSwitch-switchBase + .MuiSwitch-track": {
            backgroundColor: "#031851",
        },
        "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
            color: "#FF5968",
        },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#FFA8B1",
        },
    },
}));

const NumberFormatCustom = (props) => {
    const todos = useSelector((state) => state.todos);

    const { inputRef, value, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            value={value}
            getInputRef={inputRef}
            onValueChange={({ value: v }) => onChange({ target: { value: v } })}
            thousandSeparator={todos.amountFormat === "de-DE" ? "." : ","}
            decimalSeparator={todos.amountFormat === "de-DE" ? "," : "."}
            fixedDecimalScale={true}
            decimalScale={2}
            valueIsNumericString
        />
    );
};

export const UpdateAdd = (props) => {
    // Properties
    const {
        accountSystem,
        actives,
        clientMarket,
        expenseEst,
        isError,
        set,
        setalertType,
        setShow,
        setTexto,
        sumTotal,
        updateProject,
        values,
        showConfirm,
    } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component functions */
    const handleChange = (event) => {
        set({
            ...values,
            [event.target.name]: event.target.value,
        });
    };

    const handleChangeHoursAcc = (event) => {
        let total =
            values.addHourFee *
            parseFloat(event.target.value) *
            (isNaN(moment(values.finishDateAcc).diff(moment(values.startDateAcc)))
                ? 0
                : Math.ceil(moment(values.finishDateAcc).diff(moment(values.startDateAcc), "months", true)));
        set({
            ...values,
            [event.target.name]: event.target.value,
            baseFee: values.addHourFee * parseFloat(event.target.value),
            projectFee: total,
            projecTot: total * (1 - values.discount / 100),
        });
    };

    const handleChangeCheck = (event, value) => {
        switch (value) {
            case 0:
                if (values.checkedAROG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedAR",
                        toth: "tothAR",
                        startDate: "startDateAR",
                        finishDate: "finishDateAR",
                    });
                } else {
                    set({
                        ...values,
                        checkedAR: event.target.checked,
                        tothAR: 0,
                        startDateAR: event.target.checked ? values.initDate : null,
                        finishDateAR: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 1:
                if (values.checkedAPOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedAP",
                        toth: "tothAP",
                        startDate: "startDateAP",
                        finishDate: "finishDateAP",
                    });
                } else {
                    set({
                        ...values,
                        checkedAP: event.target.checked,
                        tothAP: 0,
                        startDateAP: event.target.checked ? values.initDate : null,
                        finishDateAP: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 2:
                if (values.checkedClassOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedClass",
                        toth: "tothClass",
                        startDate: "startDateClass",
                        finishDate: "finishDateClass",
                    });
                } else {
                    set({
                        ...values,
                        checkedClass: event.target.checked,
                        tothClass: 0,
                        startDateClass: event.target.checked ? values.initDate : null,
                        finishDateClass: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 3:
                if (values.checkedMultiOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedMulti",
                        toth: "tothMulti",
                        startDate: "startDateMulti",
                        finishDate: "finishDateMulti",
                    });
                } else {
                    set({
                        ...values,
                        checkedMulti: event.target.checked,
                        tothMulti: 0,
                        startDateMulti: event.target.checked ? values.initDate : null,
                        finishDateMulti: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 4:
                if (values.checkedPerOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedPer",
                        toth: "tothPer",
                        startDate: "startDatePer",
                        finishDate: "finishDatePer",
                    });
                } else {
                    set({
                        ...values,
                        checkedPer: event.target.checked,
                        tothPer: 0,
                        startDatePer: event.target.checked ? values.initDate : null,
                        finishDatePer: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 5:
                if (values.checkedSalesOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedSales",
                        toth: "tothSales",
                        startDate: "startDateSales",
                        finishDate: "finishDateSales",
                    });
                } else {
                    set({
                        ...values,
                        checkedSales: event.target.checked,
                        tothSales: 0,
                        startDateSales: event.target.checked ? values.initDate : null,
                        finishDateSales: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 6:
                if (values.checkedOrdConTaxOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedOrdConTax",
                        toth: "tothOrdConTax",
                        startDate: "startDateOrdConTax",
                        finishDate: "finishDateOrdConTax",
                    });
                } else {
                    set({
                        ...values,
                        checkedOrdConTax: event.target.checked,
                        tothOrdConTax: event.target.checked ? 1.5 : 0,
                        startDateOrdConTax: event.target.checked ? values.initDate : null,
                        finishDateOrdConTax: event.target.checked ? values.endDate : null,
                        disabledSpeConTax: event.target.checked,
                    });
                }
                break;
            case 7:
                if (values.checkedSpeConTaxOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedSpeConTax",
                        toth: "tothSpeConTax",
                        startDate: "startDateSpeConTax",
                        finishDate: "finishDateSpeConTax",
                    });
                } else {
                    set({
                        ...values,
                        checkedSpeConTax: event.target.checked,
                        tothSpeConTax: event.target.checked ? 5 : 0,
                        startDateSpeConTax: event.target.checked ? values.initDate : null,
                        finishDateSpeConTax: event.target.checked ? values.endDate : null,
                        disabledOrdConTax: event.target.checked,
                    });
                }
                break;
            case 9:
                if (values.checkedMuniOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedMuni",
                        toth: "tothMuni",
                        startDate: "startDateMuni",
                        finishDate: "finishDateMuni",
                    });
                } else {
                    set({
                        ...values,
                        checkedMuni: event.target.checked,
                        tothMuni: 0,
                        startDateMuni: event.target.checked ? values.initDate : null,
                        finishDateMuni: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 10:
                if (values.checkedOthersOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedOthers",
                        toth: "tothOthers",
                        nameOthers: "nameOthers",
                        startDate: "startDateOthers",
                        finishDate: "finishDateOthers",
                    });
                } else {
                    set({
                        ...values,
                        checkedOthers: event.target.checked,
                        nameOthers: "",
                        tothOthers: 0,
                        startDateOthers: event.target.checked ? values.initDate : null,
                        finishDateOthers: event.target.checked ? values.endDate : null,
                    });
                }
                break;
            case 11:
                set({
                    ...values,
                    checkedAcc: event.target.checked,
                    tothAcc: 0,
                    startDateAcc: event.target.checked ? values.initDate : null,
                    finishDateAcc: event.target.checked ? values.endDate : null,
                    projectFee: 0,
                    projecTot: 0,
                });
                break;
            default:
                if (values.checkedChecksOG && !event.target.checked && !todos.remind) {
                    showConfirm({
                        check: "checkedChecks",
                        toth: "tothChecks",
                        startDate: "startDateChecks",
                        finishDate: "finishDateChecks",
                    });
                } else {
                    set({
                        ...values,
                        checkedChecks: event.target.checked,
                        tothChecks: 0,
                        startDateChecks: event.target.checked ? values.initDate : null,
                        finishDateChecks: event.target.checked ? values.endDate : null,
                    });
                }
                break;
        }
    };

    const handleChangeStartDateAdd = (date, val) => {
        let startDate = moment(date).startOf("month");
        switch (val) {
            case 0:
                if (!date || !values.finishDateAR) {
                    set({
                        ...values,
                        startDateAR: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateAR))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateAR: startDate,
                        });
                    }
                }
                break;
            case 1:
                if (!date || !values.finishDateAP) {
                    set({
                        ...values,
                        startDateAP: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateAP))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateAP: startDate,
                        });
                    }
                }
                break;
            case 2:
                if (!date || !values.finishDateClass) {
                    set({
                        ...values,
                        startDateClass: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateClass))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateClass: startDate,
                        });
                    }
                }
                break;
            case 3:
                if (!date || !values.finishDateMulti) {
                    set({
                        ...values,
                        startDateMulti: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateMulti))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateMulti: startDate,
                        });
                    }
                }
                break;
            case 4:
                if (!date || !values.finishDatePer) {
                    set({
                        ...values,
                        startDatePer: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDatePer))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDatePer: startDate,
                        });
                    }
                }
                break;
            case 5:
                if (!date || !values.finishDateSales) {
                    set({
                        ...values,
                        startDateSales: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateSales))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateSales: startDate,
                        });
                    }
                }
                break;
            case 6:
                if (!date || !values.finishDateOrdConTax) {
                    set({
                        ...values,
                        startDateOrdConTax: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateOrdConTax))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateOrdConTax: startDate,
                        });
                    }
                }
                break;
            case 7:
                if (!date || !values.finishDateSpeConTax) {
                    set({
                        ...values,
                        startDateSpeConTax: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateSpeConTax))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateSpeConTax: startDate,
                        });
                    }
                }
                break;
            case 8:
                if (!date || !values.finishDateVatTax) {
                    set({
                        ...values,
                        startDateVatTax: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateVatTax))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateVatTax: startDate,
                        });
                    }
                }
                break;
            case 9:
                if (!date || !values.finishDateMuni) {
                    set({
                        ...values,
                        startDateMuni: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateMuni))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateMuni: startDate,
                        });
                    }
                }
                break;
            case 10:
                if (!date || !values.finishDateOthers) {
                    set({
                        ...values,
                        startDateOthers: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateOthers))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateOthers: startDate,
                        });
                    }
                }
                break;
            case 11:
                let total =
                    values.addHourFee *
                    parseFloat(values.tothAcc) *
                    (isNaN(moment(values.finishDateAcc).diff(startDate))
                        ? 0
                        : Math.ceil(moment(values.finishDateAcc).diff(startDate, "months", true)));

                if (!date || !values.finishDateAcc) {
                    set({
                        ...values,
                        startDateAcc: date ? startDate : date,
                        months: Math.ceil(moment(values.finishDateAcc).diff(startDate, "months", true)),
                        projectFee: total,
                        projecTot: total * (1 - values.discount / 100),
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateAcc))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateAcc: startDate,
                            months: Math.ceil(moment(values.finishDateAcc).diff(startDate, "months", true)),
                            projectFee: total,
                            projecTot: total * (1 - values.discount / 100),
                        });
                    }
                }
                break;
            default:
                if (!date || !values.finishDateChecks) {
                    set({
                        ...values,
                        startDateChecks: date ? startDate : date,
                    });
                } else {
                    if (startDate.isAfter(moment(values.finishDateChecks))) {
                        setTexto(t("reports.initDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            startDateChecks: startDate,
                        });
                    }
                }
                break;
        }
    };

    const handleChangeEndDateAdd = (date, val) => {
        let endDate = moment(date).endOf("month").startOf("day");
        switch (val) {
            case 0:
                if (!date || !values.startDateAR) {
                    set({
                        ...values,
                        finishDateAR: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateAR))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateAR: endDate,
                        });
                    }
                }
                break;
            case 1:
                if (!date || !values.startDateAP) {
                    set({
                        ...values,
                        finishDateAP: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateAP))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateAP: endDate,
                        });
                    }
                }
                break;
            case 2:
                if (!date || !values.startDateClass) {
                    set({
                        ...values,
                        finishDateClass: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateClass))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateClass: endDate,
                        });
                    }
                }
                break;
            case 3:
                if (!date || !values.startDateMulti) {
                    set({
                        ...values,
                        finishDateMulti: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateMulti))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateMulti: endDate,
                        });
                    }
                }
                break;
            case 4:
                if (!date || !values.startDatePer) {
                    set({
                        ...values,
                        finishDatePer: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDatePer))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDatePer: endDate,
                        });
                    }
                }
                break;
            case 5:
                if (!date || !values.startDateSales) {
                    set({
                        ...values,
                        finishDateSales: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateSales))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateSales: endDate,
                        });
                    }
                }
                break;
            case 6:
                if (!date || !values.startDateOrdConTax) {
                    set({
                        ...values,
                        finishDateOrdConTax: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateOrdConTax))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateOrdConTax: endDate,
                        });
                    }
                }
                break;
            case 7:
                if (!date || !values.startDateSpeConTax) {
                    set({
                        ...values,
                        finishDateSpeConTax: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateSpeConTax))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateSpeConTax: endDate,
                        });
                    }
                }
                break;
            case 9:
                if (!date || !values.startDateMuni) {
                    set({
                        ...values,
                        finishDateMuni: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateMuni))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateMuni: endDate,
                        });
                    }
                }
                break;
            case 10:
                if (!date || !values.startDateOthers) {
                    set({
                        ...values,
                        finishDateOthers: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateOthers))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateOthers: endDate,
                        });
                    }
                }
                break;
            case 11:
                let total =
                    values.addHourFee *
                    parseFloat(values.tothAcc) *
                    (isNaN(endDate.diff(moment(values.startDateAcc)))
                        ? 0
                        : Math.ceil(endDate.diff(moment(values.startDateAcc), "months", true)));

                if (!date || !values.startDateAcc) {
                    set({
                        ...values,
                        finishDateAcc: date ? endDate : date,
                        months: Math.ceil(endDate.diff(moment(values.startDateAcc), "months", true)),
                        projectFee: total,
                        projecTot: total * (1 - values.discount / 100),
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateAcc))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateAcc: endDate,
                            months: Math.ceil(endDate.diff(moment(values.startDateAcc), "months", true)),
                            projectFee: total,
                            projecTot: total * (1 - values.discount / 100),
                        });
                    }
                }
                break;
            default:
                if (!date || !values.startDateChecks) {
                    set({
                        ...values,
                        finishDateChecks: date ? endDate : date,
                    });
                } else {
                    if (endDate.isBefore(moment(values.startDateChecks))) {
                        setTexto(t("reports.endDateMess"));
                        setalertType("warning");
                        setShow(true);
                    } else {
                        set({
                            ...values,
                            finishDateChecks: endDate,
                        });
                    }
                }
                break;
        }
    };

    return (
        <StyledContainer>
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={t("language.locale")}
                localeText={{ clearButtonLabel: t("miscellaneous.clear") }}
            >
                <Card elevation={0}>
                    <CardHeader
                        title={t("services.additionals")}
                        sx={{ color: "#ff5968" }}
                    />
                    <Divider />
                    <CardContent sx={{ pb: 5 }}>
                        <Grid container spacing={2} alignItems="center">
                            {values.checkHour && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedAcc"
                                                    checked={values.checkedAcc}
                                                    onChange={() => {
                                                        return false;
                                                    }}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                />
                                            }
                                            label="Accountant"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={values.updateBoolean || !values.initDate || !values.endDate}
                                            label={t("miscellaneous.hoursMonth")}
                                            name="tothAcc"
                                            onChange={handleChangeHoursAcc}
                                            value={values.tothAcc}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 0,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedAcc && isError && values.tothAcc === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.initDate || !values.endDate}
                                            value={values.startDateAcc}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 11)}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedAcc}
                                                    {...params}
                                                    error={values.checkedAcc && isError && !values.startDateAcc}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.initDate || !values.endDate}
                                            value={values.finishDateAcc}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 11)}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedAcc}
                                                    {...params}
                                                    error={values.checkedAcc && isError && !values.finishDateAcc}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                </>
                            )}
                            {clientMarket === "Estados Unidos" && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedChecks"
                                                    checked={values.checkedChecks}
                                                    onChange={(value) => handleChangeCheck(value, 12)}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                    disabled={
                                                        values.updateBoolean || !values.initDate || !values.endDate
                                                    }
                                                />
                                            }
                                            label="Checks"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={values.updateBoolean || !values.checkedChecks}
                                            label={t("miscellaneous.hoursMonth")}
                                            name="tothChecks"
                                            onChange={handleChange}
                                            value={values.tothChecks}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 0,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedChecks && isError && values.tothChecks === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.checkedChecks}
                                            value={values.startDateChecks}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 12)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedChecks}
                                                    {...params}
                                                    error={values.checkedChecks && isError && !values.startDateChecks}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.checkedChecks}
                                            value={values.finishDateChecks}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 12)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedChecks}
                                                    {...params}
                                                    error={values.checkedChecks && isError && !values.finishDateChecks}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="checkedAR"
                                            checked={values.checkedAR}
                                            onChange={(value) => handleChangeCheck(value, 0)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={values.updateBoolean || !values.initDate || !values.endDate}
                                        />
                                    }
                                    label="A/R"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={values.updateBoolean || !values.checkedAR}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothAR"
                                    onChange={handleChange}
                                    value={values.tothAR}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedAR && isError && values.tothAR === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedAR}
                                    value={values.startDateAR}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 0)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAR}
                                            {...params}
                                            error={values.checkedAR && isError && !values.startDateAR}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedAR}
                                    value={values.finishDateAR}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 0)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAR}
                                            {...params}
                                            error={values.checkedAR && isError && !values.finishDateAR}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="checkedAP"
                                            checked={values.checkedAP}
                                            onChange={(value) => handleChangeCheck(value, 1)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={values.updateBoolean || !values.initDate || !values.endDate}
                                        />
                                    }
                                    label="A/P"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={values.updateBoolean || !values.checkedAP}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothAP"
                                    onChange={handleChange}
                                    value={values.tothAP}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedAP && isError && values.tothAP === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedAP}
                                    value={values.startDateAP}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 1)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAP}
                                            {...params}
                                            error={values.checkedAP && isError && !values.startDateAP}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedAP}
                                    value={values.finishDateAP}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 1)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedAP}
                                            {...params}
                                            error={values.checkedAP && isError && !values.finishDateAP}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="checkedClass"
                                            checked={values.checkedClass}
                                            onChange={(value) => handleChangeCheck(value, 2)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={values.updateBoolean || !values.initDate || !values.endDate}
                                        />
                                    }
                                    label={t("services.classesCategories")}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={values.updateBoolean || !values.checkedClass}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothClass"
                                    onChange={handleChange}
                                    value={values.tothClass}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedClass && isError && values.tothClass === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedClass}
                                    value={values.startDateClass}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 2)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedClass}
                                            {...params}
                                            error={values.checkedClass && isError && !values.startDateClass}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedClass}
                                    value={values.finishDateClass}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 2)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedClass}
                                            {...params}
                                            error={values.checkedClass && isError && !values.finishDateClass}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="checkedMulti"
                                            checked={values.checkedMulti}
                                            onChange={(value) => handleChangeCheck(value, 3)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={values.updateBoolean || !values.initDate || !values.endDate}
                                        />
                                    }
                                    label={t("services.multiEntity")}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={values.updateBoolean || !values.checkedMulti}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothMulti"
                                    onChange={handleChange}
                                    value={values.tothMulti}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedMulti && isError && values.tothMulti === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedMulti}
                                    value={values.startDateMulti}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 3)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedMulti}
                                            {...params}
                                            error={values.checkedMulti && isError && !values.startDateMulti}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedMulti}
                                    value={values.finishDateMulti}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 3)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedMulti}
                                            {...params}
                                            error={values.checkedMulti && isError && !values.finishDateMulti}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="checkedPer"
                                            checked={values.checkedPer}
                                            onChange={(value) => handleChangeCheck(value, 4)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={values.updateBoolean || !values.initDate || !values.endDate}
                                        />
                                    }
                                    label={t("services.personalizedReporting")}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={values.updateBoolean || !values.checkedPer}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothPer"
                                    onChange={handleChange}
                                    value={values.tothPer}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedPer && isError && values.tothPer === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedPer}
                                    value={values.startDatePer}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 4)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedPer}
                                            {...params}
                                            error={values.checkedPer && isError && !values.startDatePer}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedPer}
                                    value={values.finishDatePer}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 4)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedPer}
                                            {...params}
                                            error={values.checkedPer && isError && !values.finishDatePer}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            {clientMarket === "Estados Unidos" && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedSales"
                                                    checked={values.checkedSales}
                                                    onChange={(value) => handleChangeCheck(value, 5)}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                    disabled={
                                                        values.updateBoolean || !values.initDate || !values.endDate
                                                    }
                                                />
                                            }
                                            label={t("services.salesTaxes")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={values.updateBoolean || !values.checkedSales}
                                            label={t("miscellaneous.hoursMonth")}
                                            name="tothSales"
                                            onChange={handleChange}
                                            value={values.tothSales}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 0,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedSales && isError && values.tothSales === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.checkedSales}
                                            value={values.startDateSales}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 5)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedSales}
                                                    {...params}
                                                    error={values.checkedSales && isError && !values.startDateSales}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.checkedSales}
                                            value={values.finishDateSales}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 5)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedSales}
                                                    {...params}
                                                    error={values.checkedSales && isError && !values.finishDateSales}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                </>
                            )}
                            {clientMarket === "Venezuela" && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedOrdConTax"
                                                    checked={values.checkedOrdConTax}
                                                    onChange={(value) => handleChangeCheck(value, 6)}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                    disabled={
                                                        values.updateBoolean ||
                                                        !values.initDate ||
                                                        !values.endDate ||
                                                        values.disabledOrdConTax
                                                    }
                                                />
                                            }
                                            label={t("proposals.addOrdTax")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={values.updateBoolean || !values.checkedOrdConTax}
                                            label={t("miscellaneous.hoursMonth")}
                                            name="tothOrdConTax"
                                            onChange={handleChange}
                                            value={values.tothOrdConTax}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 1.5,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedOrdConTax && isError && values.tothOrdConTax === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.checkedOrdConTax}
                                            value={values.startDateOrdConTax}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 6)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedOrdConTax}
                                                    {...params}
                                                    error={
                                                        values.checkedOrdConTax && isError && !values.startDateOrdConTax
                                                    }
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.checkedOrdConTax}
                                            value={values.finishDateOrdConTax}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 6)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedOrdConTax}
                                                    {...params}
                                                    error={
                                                        values.checkedOrdConTax &&
                                                        isError &&
                                                        !values.finishDateOrdConTax
                                                    }
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedSpeConTax"
                                                    checked={values.checkedSpeConTax}
                                                    onChange={(value) => handleChangeCheck(value, 7)}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                    disabled={
                                                        values.updateBoolean ||
                                                        !values.initDate ||
                                                        !values.endDate ||
                                                        values.disabledSpeConTax
                                                    }
                                                />
                                            }
                                            label={t("proposals.addSpecial")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={values.updateBoolean || !values.checkedSpeConTax}
                                            label={t("miscellaneous.hoursMonth")}
                                            name="tothSpeConTax"
                                            onChange={handleChange}
                                            value={values.tothSpeConTax}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 3.5,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedSpeConTax && isError && values.tothSpeConTax === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.checkedSpeConTax}
                                            value={values.startDateSpeConTax}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 7)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedSpeConTax}
                                                    {...params}
                                                    error={
                                                        values.checkedSpeConTax && isError && !values.startDateSpeConTax
                                                    }
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.checkedSpeConTax}
                                            value={values.finishDateSpeConTax}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 7)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedSpeConTax}
                                                    {...params}
                                                    error={
                                                        values.checkedSpeConTax &&
                                                        isError &&
                                                        !values.finishDateSpeConTax
                                                    }
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="checkedMuni"
                                                    checked={values.checkedMuni}
                                                    onChange={(value) => handleChangeCheck(value, 9)}
                                                    inputProps={{
                                                        "aria-label": "controlled",
                                                    }}
                                                    disabled={
                                                        values.updateBoolean || !values.initDate || !values.endDate
                                                    }
                                                />
                                            }
                                            label={t("proposals.addImpMun")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            disabled={values.updateBoolean || !values.checkedMuni}
                                            label={t("miscellaneous.hoursMonth")}
                                            name="tothMuni"
                                            onChange={handleChange}
                                            value={values.tothMuni}
                                            variant="outlined"
                                            type="number"
                                            inputProps={{
                                                min: 0,
                                                step: 0.5,
                                                onKeyDown: (event) => {
                                                    event.preventDefault();
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            error={values.checkedMuni && isError && values.tothMuni === 0}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.checkedMuni}
                                            value={values.startDateMuni}
                                            onChange={(newValue) => handleChangeStartDateAdd(newValue, 9)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedMuni}
                                                    {...params}
                                                    error={values.checkedMuni && isError && !values.startDateMuni}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.initDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DatePicker
                                            componentsProps={{
                                                actionBar: {
                                                    actions: ["clear"],
                                                },
                                            }}
                                            disabled={values.updateBoolean || !values.checkedMuni}
                                            value={values.finishDateMuni}
                                            onChange={(newValue) => handleChangeEndDateAdd(newValue, 9)}
                                            minDate={moment(values.initDate).startOf("month")}
                                            maxDate={moment(values.endDate).endOf("month")}
                                            inputFormat={todos.dateFormat}
                                            renderInput={(params) => (
                                                <TextField
                                                    fullWidth
                                                    required={values.checkedMuni}
                                                    {...params}
                                                    error={values.checkedMuni && isError && !values.finishDateMuni}
                                                />
                                            )}
                                            inputProps={{ readOnly: true }}
                                            label={t("reports.endDate")}
                                            views={["year", "month"]}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} md={3}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3} xl={2} sx={{ paddingLeft: "5px !important" }}>
                                        <Checkbox
                                            name="checkedOthers"
                                            checked={values.checkedOthers}
                                            onChange={(value) => handleChangeCheck(value, 10)}
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                            disabled={values.updateBoolean || !values.initDate || !values.endDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={9} xl={10}>
                                        <TextField
                                            fullWidth
                                            disabled={values.updateBoolean || !values.checkedOthers}
                                            label={t("services.others")}
                                            name="nameOthers"
                                            onChange={handleChange}
                                            value={values.nameOthers}
                                            variant="outlined"
                                            error={values.checkedOthers && isError && !values.nameOthers}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled={values.updateBoolean || !values.checkedOthers}
                                    label={t("miscellaneous.hoursMonth")}
                                    name="tothOthers"
                                    onChange={handleChange}
                                    value={values.tothOthers}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.5,
                                        onKeyDown: (event) => {
                                            event.preventDefault();
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    error={values.checkedOthers && isError && values.tothOthers === 0}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedOthers}
                                    value={values.startDateOthers}
                                    onChange={(newValue) => handleChangeStartDateAdd(newValue, 10)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedOthers}
                                            {...params}
                                            error={values.checkedOthers && isError && !values.startDateOthers}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.initDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    disabled={values.updateBoolean || !values.checkedOthers}
                                    value={values.finishDateOthers}
                                    onChange={(newValue) => handleChangeEndDateAdd(newValue, 10)}
                                    minDate={moment(values.initDate).startOf("month")}
                                    maxDate={moment(values.endDate).endOf("month")}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required={values.checkedOthers}
                                            {...params}
                                            error={values.checkedOthers && isError && !values.finishDateOthers}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("reports.endDate")}
                                    views={["year", "month"]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}></Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    disabled
                                    label={t("services.hoursTotal")}
                                    value={sumTotal}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-hourlyRate">
                                        {t("services.hourlyRate")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-hourlyRate"
                                        value={values.addHourFee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.hourlyRate")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-hourlyRateAdd">
                                        {t("services.hourlyRateAdd")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-hourlyRateAdd"
                                        value={values.addHourFee * sumTotal}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.hourlyRateAdd")}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                    <Divider />
                    <Box className={classes.buttonContainer}>
                        <Button
                            color="secondary"
                            disabled={
                                actives.includes("Bookkeeper") ||
                                expenseEst === -1 ||
                                (accountSystem.disabledData === true &&
                                    (accountSystem.prov === "Xero" || accountSystem.prov === "Quickbooks Online"))
                            }
                            variant="contained"
                            disableElevation
                            classes={{ root: classes.btnRoot }}
                            onClick={updateProject}
                        >
                            {t("miscellaneous.save")}
                        </Button>
                    </Box>
                </Card>
            </LocalizationProvider>
        </StyledContainer>
    );
};
